/*
Copyright (C) 2023-2026 QuantumNous

This program is free software: you can redistribute it and/or modify
it under the terms of the GNU Affero General Public License as
published by the Free Software Foundation, either version 3 of the
License, or (at your option) any later version.

This program is distributed in the hope that it will be useful,
but WITHOUT ANY WARRANTY; without even the implied warranty of
MERCHANTABILITY or FITNESS FOR A PARTICULAR PURPOSE. See the
GNU Affero General Public License for more details.

You should have received a copy of the GNU Affero General Public License
along with this program. If not, see <https://www.gnu.org/licenses/>.

For commercial licensing, please contact support@quantumnous.com
*/
import { Link } from '@tanstack/react-router'
import {
  AlertTriangle,
  BookOpenText,
  Clock3,
  MessageSquareText,
  Video,
} from 'lucide-react'
import { useMemo } from 'react'
import { useTranslation } from 'react-i18next'
import type { BundledLanguage } from 'shiki'

import {
  CodeBlock,
  CodeBlockCopyButton,
} from '@/components/ai-elements/code-block'
import { StaticDataTable } from '@/components/data-table'
import { PublicLayout } from '@/components/layout'
import { Alert, AlertDescription, AlertTitle } from '@/components/ui/alert'
import { Badge } from '@/components/ui/badge'
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs'
import { useStatus } from '@/hooks/use-status'
import { cn } from '@/lib/utils'

import {
  API_DOCUMENTS,
  findApiDocument,
  type ApiDocDefinition,
  type ApiDocId,
} from './api-docs-data'

type ParameterRow = {
  name: string
  type: string
  required: boolean
  descriptionKey: string
}

const CODE_LANGUAGE: BundledLanguage = 'bash'

function useBaseUrl(): string {
  const { status } = useStatus()

  return useMemo(() => {
    const statusRecord = status as Record<string, unknown> | null
    const statusData = status?.data as Record<string, unknown> | undefined
    const candidate =
      statusRecord?.server_address ??
      statusRecord?.serverAddress ??
      statusData?.server_address ??
      statusData?.serverAddress

    if (typeof candidate === 'string' && candidate.trim()) {
      return candidate.replace(/\/$/, '')
    }
    if (typeof window !== 'undefined') return window.location.origin
    return 'https://api.example.com'
  }, [status])
}

function curlSubmit(baseUrl: string, payload: Record<string, unknown>): string {
  return [
    `curl -X POST ${baseUrl}/v1/videos \\`,
    '  -H "Authorization: Bearer $NEW_API_KEY" \\',
    '  -H "Content-Type: application/json" \\',
    '  -H "Idempotency-Key: $VIDEO_IDEMPOTENCY_KEY" \\',
    `  -d '${JSON.stringify(payload, null, 2).replaceAll('\n', '\n     ')}'`,
  ].join('\n')
}

function curlWan3Submit(
  baseUrl: string,
  payload: Record<string, unknown>
): string {
  return [
    `curl -X POST ${baseUrl}/v1/videos \\`,
    '  -H "Authorization: Bearer $NEW_API_KEY" \\',
    '  -H "Content-Type: application/json" \\',
    `  -d '${JSON.stringify(payload, null, 2).replaceAll('\n', '\n     ')}'`,
  ].join('\n')
}

function curlImageSubmit(
  baseUrl: string,
  payload: Record<string, unknown>
): string {
  return [
    `curl -X POST ${baseUrl}/v1/images/generations \\`,
    '  -H "Authorization: Bearer $NEW_API_KEY" \\',
    '  -H "Content-Type: application/json" \\',
    `  -d '${JSON.stringify(payload, null, 2).replaceAll('\n', '\n     ')}'`,
  ].join('\n')
}

function curlImageEdit(baseUrl: string, model: string): string {
  return [
    `curl -X POST ${baseUrl}/v1/images/edits \\`,
    '  -H "Authorization: Bearer $NEW_API_KEY" \\',
    `  -F 'model=${model}' \\`,
    "  -F 'prompt=keep the subject and replace the background with a clean studio' \\",
    "  -F 'image=@input.png' \\",
    "  -F 'n=1' \\",
    "  -F 'size=1024x1024' \\",
    "  -F 'response_format=url'",
  ].join('\n')
}

function curlImageEditURL(baseUrl: string, model: string): string {
  return [
    `curl -X POST ${baseUrl}/v1/images/edits \\`,
    '  -H "Authorization: Bearer $NEW_API_KEY" \\',
    '  -H "Content-Type: application/json" \\',
    `  -d '${JSON.stringify(
      {
        model,
        prompt:
          'keep the subject and replace the background with a clean studio',
        image_url: ['https://example.com/input.png'],
        size: '2048x2048',
        size_tier: '2k',
        response_format: 'url',
      },
      null,
      2
    ).replaceAll('\\n', '\\n     ')}'`,
  ].join('\\n')
}

function curlGeminiImageGenerate(baseUrl: string, model: string): string {
  return [
    `curl -X POST ${baseUrl}/v1beta/models/${model}:generateContent \\`,
    '  -H "Authorization: Bearer $NEW_API_KEY" \\',
    '  -H "Content-Type: application/json" \\',
    `  -d '${JSON.stringify(
      {
        contents: [
          {
            role: 'user',
            parts: [{ text: 'Create a clean studio product photograph.' }],
          },
        ],
        systemInstruction: {
          parts: [{ text: 'Return a polished product image.' }],
        },
        generationConfig: {
          temperature: 0.7,
          maxOutputTokens: 512,
          responseModalities: ['TEXT', 'IMAGE'],
          imageConfig: {
            aspectRatio: '16:9',
            imageSize: '1K',
          },
        },
      },
      null,
      2
    ).replaceAll('\n', '\n     ')}'`,
  ].join('\n')
}

function curlResponse(baseUrl: string, model: string): string {
  return [
    `curl -X POST ${baseUrl}/v1/responses \\`,
    '  -H "Authorization: Bearer $NEW_API_KEY" \\',
    '  -H "Content-Type: application/json" \\',
    `  -d '${JSON.stringify(
      {
        model,
        input: 'Reply with OK only.',
        max_output_tokens: 16,
      },
      null,
      2
    ).replaceAll('\n', '\n     ')}'`,
  ].join('\n')
}

function curlClaudeMessage(baseUrl: string, model: string): string {
  return [
    `curl -X POST ${baseUrl}/v1/messages \\`,
    '  -H "x-api-key: $NEW_API_KEY" \\',
    '  -H "anthropic-version: 2023-06-01" \\',
    '  -H "content-type: application/json" \\',
    `  -d '${JSON.stringify(
      {
        model,
        max_tokens: 16,
        messages: [{ role: 'user', content: 'Reply with OK only.' }],
      },
      null,
      2
    ).replaceAll('\n', '\n     ')}'`,
  ].join('\n')
}

function curlPoll(baseUrl: string): string {
  return [
    `curl ${baseUrl}/v1/videos/task_xxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxx \\`,
    '  -H "Authorization: Bearer $NEW_API_KEY"',
  ].join('\n')
}

function processingResponse(
  model: string,
  duration: number,
  image: boolean
): string {
  const status = image ? 'processing' : 'in_progress'
  return JSON.stringify(
    {
      id: 'task_xxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxx',
      task_id: 'task_xxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxx',
      object: 'video',
      created_at: 1714200000000,
      status,
      state: status,
      task_status: status,
      progress: 45,
      model,
      video_url: null,
      metadata: { result_urls: [] },
      seconds: String(duration),
      duration,
      aspect_ratio: '16:9',
      success: false,
      final: false,
    },
    null,
    2
  )
}

function completedResponse(
  model: string,
  duration: number,
  resultField: 'video_url' | 'image_url' = 'video_url'
): string {
  const resultUrl = 'https://example.com/generated/result.mp4'
  return JSON.stringify(
    {
      id: 'task_xxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxx',
      task_id: 'task_xxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxx',
      object: 'video',
      created_at: 1714200000000,
      completed_at: 1714200180000,
      status: 'completed',
      state: 'completed',
      task_status: 'completed',
      progress: 100,
      model,
      [resultField]: resultUrl,
      url: resultUrl,
      metadata: { result_urls: [resultUrl] },
      seconds: String(duration),
      duration,
      aspect_ratio: '16:9',
      success: true,
      final: true,
    },
    null,
    2
  )
}

function failedResponse(model: string): string {
  return JSON.stringify(
    {
      id: 'task_xxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxx',
      task_id: 'task_xxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxx',
      object: 'video',
      status: 'failed',
      state: 'failed',
      task_status: 'failed',
      progress: 0,
      model,
      success: false,
      final: true,
      error: { message: 'Task failed', code: 'task_failed' },
    },
    null,
    2
  )
}

function commonAsyncParameters(duration: number): ParameterRow[] {
  return [
    {
      name: 'model',
      type: 'string',
      required: true,
      descriptionKey: 'The exact model identifier for this request.',
    },
    {
      name: 'prompt',
      type: 'string',
      required: true,
      descriptionKey: 'A text description of the requested result.',
    },
    {
      name: 'duration | seconds',
      type: 'integer',
      required: true,
      descriptionKey:
        duration === 8
          ? 'The duration is fixed at 8 seconds. Other values return 400.'
          : 'The duration is fixed at 10 seconds. Other values return 400.',
    },
    {
      name: 'aspect_ratio',
      type: 'string',
      required: false,
      descriptionKey:
        '16:9 or 9:16. Omit it to use the default landscape output.',
    },
  ]
}

function videoParameters(document: ApiDocDefinition): ParameterRow[] {
  const params = commonAsyncParameters(document.duration ?? 10)
  const isVeo31 = document.id.startsWith('veo-3-1')
  const isVideoEdit = document.id.startsWith('veo-omni-flash-video-edit')
  const resolution = document.resolutions?.join(' | ') ?? '720p | 1080p | 4k'
  const resolutionParameter: ParameterRow = {
    name: 'resolution',
    type: resolution,
    required: true,
    descriptionKey: '输出分辨率，仅支持 720p、1080p、4k。',
  }

  if (isVeo31) {
    return [
      ...params,
      resolutionParameter,
      {
        name: 'images',
        type: 'string[]',
        required: false,
        descriptionKey:
          '一张图片可用于图生视频；两张按顺序传入的图片分别为首帧和尾帧。',
      },
      {
        name: 'Ingredients_images',
        type: 'string[]',
        required: false,
        descriptionKey:
          '可选多图参考图片 URL，最多 8 张；不可与首尾帧同时使用。',
      },
    ]
  }
  if (isVideoEdit) {
    return [
      ...params,
      resolutionParameter,
      {
        name: 'video_url',
        type: 'string',
        required: true,
        descriptionKey: '一个可公开访问的视频参考 URL。',
      },
      {
        name: 'Ingredients_images',
        type: 'string[]',
        required: false,
        descriptionKey: '编辑视频时可选的多图参考 URL，最多 6 张。',
      },
    ]
  }
  return [
    ...params,
    resolutionParameter,
    {
      name: 'Ingredients_images',
      type: 'string[]',
      required: false,
      descriptionKey:
        '多图参考图片 URL，最多 6 张；Omni 不支持首帧和尾帧字段。',
    },
  ]
}

function sd25VideoParameters(): ParameterRow[] {
  return [
    {
      name: 'model',
      type: 'string',
      required: true,
      descriptionKey: 'Use sd2.5-720p-standard.',
    },
    {
      name: 'prompt',
      type: 'string',
      required: true,
      descriptionKey: 'Required text prompt for the video.',
    },
    {
      name: 'duration | seconds',
      type: 'integer | numeric string',
      required: false,
      descriptionKey:
        'Video duration in seconds. Supports integers from 4 to 29 and defaults to 4; duration takes precedence over seconds.',
    },
    {
      name: 'aspect_ratio | aspectRatio',
      type: 'string',
      required: false,
      descriptionKey:
        'Defaults to 16:9. Supported values: 21:9, 16:9, 4:3, 1:1, 3:4, 9:16, 1280x720, or 720x1280.',
    },
    {
      name: 'resolution',
      type: 'string',
      required: false,
      descriptionKey:
        'The model outputs 720p only. When supplied, this value must be 720p.',
    },
    {
      name: 'extra_images',
      type: 'string[]',
      required: false,
      descriptionKey:
        'Publicly accessible reference image URLs. Duplicate URLs are removed; at most 30 remain.',
    },
    {
      name: 'extra_videos',
      type: 'string[]',
      required: false,
      descriptionKey:
        'Publicly accessible reference video URLs. Duplicate URLs are removed; at most 10 remain.',
    },
    {
      name: 'extra_audios',
      type: 'string[]',
      required: false,
      descriptionKey:
        'Publicly accessible reference audio URLs. Duplicate URLs are removed; at most 10 remain.',
    },
  ]
}

function wan3VideoParameters(): ParameterRow[] {
  return [
    {
      name: 'model',
      type: 'string',
      required: true,
      descriptionKey: 'Use wan3.0-video.',
    },
    {
      name: 'prompt',
      type: 'string',
      required: true,
      descriptionKey:
        'Required top-level prompt. It cannot be blank; keep it identical to metadata.input.prompt to avoid ambiguity.',
    },
    {
      name: 'duration',
      type: 'integer',
      required: false,
      descriptionKey:
        'Recommended top-level duration in seconds. Use an integer from 2 to 30 and keep it identical to metadata.parameters.duration.',
    },
    {
      name: 'metadata',
      type: 'object',
      required: false,
      descriptionKey:
        'Wan3.0-specific input and parameters. Send a JSON object; do not serialize it as a string.',
    },
    {
      name: 'metadata.input.prompt',
      type: 'string',
      required: false,
      descriptionKey:
        'Final prompt sent upstream. Supports 1-20000 characters and falls back to the top-level prompt when omitted.',
    },
    {
      name: 'metadata.input.media',
      type: 'object[]',
      required: false,
      descriptionKey:
        'Optional reference media. Accepts up to 10 images, 5 videos, and 5 audio files, counted separately.',
    },
    {
      name: 'metadata.input.media[].type',
      type: 'string',
      required: false,
      descriptionKey:
        'Use reference_image, first_frame, last_frame, reference_video, reference_audio, or reference_voice.',
    },
    {
      name: 'metadata.input.media[].url',
      type: 'string',
      required: false,
      descriptionKey:
        'Public HTTP(S) direct link to the file. Login pages, authenticated URLs, local paths, blob URLs, Base64 data URLs, and file IDs are not supported.',
    },
    {
      name: 'metadata.parameters.resolution',
      type: 'string',
      required: false,
      descriptionKey:
        'Output resolution and billing tier. Use 480P (¥0.37/s), 720P (¥0.43/s), or 1080P (¥0.48/s); defaults to 720P.',
    },
    {
      name: 'metadata.parameters.ratio',
      type: 'string',
      required: false,
      descriptionKey:
        'Output aspect ratio. Supports adaptive, 16:9, 9:16, 1:1, 4:3, and 3:4; defaults to 16:9.',
    },
    {
      name: 'metadata.parameters.duration',
      type: 'integer',
      required: false,
      descriptionKey:
        'Duration in seconds, from 2 to 30. It must match top-level duration or seconds when both are supplied; the default is 5.',
    },
    {
      name: 'metadata.parameters.audio | prompt_extend | watermark',
      type: 'boolean',
      required: false,
      descriptionKey:
        'Optional upstream switches. When omitted, the public API leaves each choice to the upstream default.',
    },
    {
      name: 'metadata.parameters.seed',
      type: 'integer',
      required: false,
      descriptionKey:
        'Optional random seed from 0 to 2147483647. Omit it to let the upstream service choose.',
    },
  ]
}

function miniMaxH3VideoParameters(): ParameterRow[] {
  return [
    {
      name: 'model',
      type: 'string',
      required: true,
      descriptionKey: 'The exact model ID. Use minimax-h3.',
    },
    {
      name: 'prompt',
      type: 'string',
      required: true,
      descriptionKey:
        'Required non-empty text prompt. The web input limit is 7000 characters.',
    },
    {
      name: 'duration | seconds',
      type: 'integer',
      required: false,
      descriptionKey:
        'Integer duration in seconds from 4 to 15. Defaults to 4 when omitted.',
    },
    {
      name: 'resolution',
      type: 'string',
      required: false,
      descriptionKey: 'Output resolution. Use 768P or 2K.',
    },
    {
      name: 'aspect_ratio | ratio',
      type: 'string',
      required: false,
      descriptionKey:
        'Output aspect ratio. Supports adaptive, 21:9, 16:9, 4:3, 1:1, 3:4, and 9:16.',
    },
    {
      name: 'generation_mode',
      type: 'string',
      required: false,
      descriptionKey:
        'Use frames for text or first/last frame input, and references for image, video, or audio references.',
    },
    {
      name: 'image_urls',
      type: 'string[]',
      required: false,
      descriptionKey:
        'Public image URLs. Up to 9 images; the first 5 are free and each additional image costs ¥0.05.',
    },
    {
      name: 'video_urls',
      type: 'string[]',
      required: false,
      descriptionKey:
        'Public video URLs. Up to 3 videos, each 2-15 seconds; combined duration must not exceed 15 seconds. The surcharge is ¥0.09 per second at 768P or ¥0.15 per second at 2K.',
    },
    {
      name: 'audio_urls',
      type: 'string[]',
      required: false,
      descriptionKey:
        'Public audio URLs. Up to 3 files; combined duration must not exceed 15 seconds. No audio surcharge is applied.',
    },
    {
      name: 'content',
      type: 'object[]',
      required: false,
      descriptionKey:
        'Advanced multimodal content. Use text, image_url, video_url, and audio_url entries with their documented roles; a non-empty content array replaces the simplified fields.',
    },
    {
      name: 'first_image | last_image',
      type: 'string',
      required: false,
      descriptionKey:
        'Frame URLs for frames mode. Send first_image and optionally last_image; do not mix frame inputs with reference videos.',
    },
    {
      name: 'generate_audio',
      type: 'boolean',
      required: false,
      descriptionKey:
        'Native audio is always generated for MiniMax H3; send true explicitly when using the public API.',
    },
  ]
}

function KlingVideoDocContent(props: { baseUrl: string }) {
  const { t } = useTranslation()
  const payload = {
    model: 'kling-4.0',
    prompt: 'A red ceramic cup on a white table, camera slowly moves closer.',
    duration: 10,
    ratio: '16:9',
    resolution: '720p',
  }
  const referencePayload = {
    ...payload,
    referenceImages: ['https://media.example.com/reference.jpg'],
    referenceVideos: ['https://media.example.com/reference.mp4'],
    referenceAudios: ['https://media.example.com/reference.mp3'],
  }
  return (
    <div className='flex flex-col gap-6'>
      <CodeSample
        title={t('Authentication')}
        code='export NEW_API_KEY="YOUR_API_KEY"'
      />
      <p>
        {t(
          'Kling 4.0 supports 3-30 second videos, all aspect ratios, 720p and 1080p output, and mixed image, video and audio references.'
        )}
      </p>
      <p>
        {t(
          'Kling accepts up to 10 images, 5 videos and 3 audio files. Each image is limited to 10 MB; each video to 50 MB and 30 seconds; each audio file to 20 MB and 5-30 seconds. Use public HTTP(S) media URLs. Reference arrays are optional; omit them for text-to-video.'
        )}
      </p>
      <p>
        {t(
          'Kling costs ¥0.35/s at 720p or ¥0.43/s at 1080p, multiplied by the applicable group factor. Billable seconds equal generation seconds plus the actual total duration of all reference videos, including fractional seconds. Generating 10 seconds with 5 seconds of reference video costs ¥5.25 at 720p or ¥6.45 at 1080p before any group factor.'
        )}
      </p>
      <section className='space-y-3'>
        <SectionHeading>{t('Request parameters')}</SectionHeading>
        <ParameterTable
          rows={[
            {
              name: 'model',
              type: 'string',
              required: true,
              descriptionKey: 'Use kling-4.0.',
            },
            {
              name: 'prompt',
              type: 'string',
              required: true,
              descriptionKey: 'A text description of the requested result.',
            },
            {
              name: 'duration',
              type: 'integer',
              required: false,
              descriptionKey:
                'Kling generation duration: 3-30 integer seconds; defaults to 5.',
            },
            {
              name: 'ratio',
              type: 'string',
              required: false,
              descriptionKey:
                'Kling supports all positive integer width:height ratios, such as 16:9, 9:16 or 7:5; defaults to 16:9.',
            },
            {
              name: 'resolution',
              type: 'string',
              required: false,
              descriptionKey:
                'Kling output resolution: 720p or 1080p; defaults to 720p.',
            },
            {
              name: 'referenceImages',
              type: 'string[]',
              required: false,
              descriptionKey:
                'Up to 10 public image URLs; each image must not exceed 10 MB.',
            },
            {
              name: 'referenceVideos',
              type: 'string[]',
              required: false,
              descriptionKey:
                'Up to 5 public video URLs; each video must not exceed 50 MB or 30 seconds. All actual reference video seconds are added to billable duration.',
            },
            {
              name: 'referenceAudios',
              type: 'string[]',
              required: false,
              descriptionKey:
                'Up to 3 public audio URLs; each file must not exceed 20 MB and must be 5-30 seconds long.',
            },
          ]}
        />
      </section>
      <CodeSample
        title={t('Text to video')}
        code={curlWan3Submit(props.baseUrl, payload)}
      />
      <CodeSample
        title={t('Kling image, video and audio references')}
        code={curlWan3Submit(props.baseUrl, referencePayload)}
      />
      <p>
        {t(
          'Set duration to an integer from 3 to 30 (default 5), resolution to 720p or 1080p (default 720p), and ratio to positive width:height (default 16:9). Do not send frame-role fields or metadata; use referenceImages, referenceVideos and referenceAudios.'
        )}
      </p>
      <CodeSample
        title={t('Kling task submission response (example)')}
        language='json'
        code={JSON.stringify(
          {
            id: 'task_example',
            object: 'video',
            model: 'kling-4.0',
            status: 'queued',
          },
          null,
          2
        )}
      />
      <CodeSample
        title={t('Query task')}
        code={`export TASK_ID="task_example"\ncurl "${props.baseUrl}/v1/videos/$TASK_ID" -H "Authorization: Bearer $NEW_API_KEY"`}
      />
      <CodeSample
        title={t('Kling completed task response (example)')}
        language='json'
        code={JSON.stringify(
          {
            id: 'task_example',
            object: 'video',
            model: 'kling-4.0',
            status: 'completed',
          },
          null,
          2
        )}
      />
      <p>
        {t(
          'Replace task_example with the id returned by submission. Response examples show only the fields needed for polling; queued or in_progress means keep waiting, completed or succeeded means ready to download, and failed means stop and inspect the error.'
        )}
      </p>
      <CodeSample
        title={t('Download video')}
        code={`curl --location "${props.baseUrl}/v1/videos/$TASK_ID/content" -H "Authorization: Bearer $NEW_API_KEY" --output result.mp4`}
      />
      <p>
        {t(
          'Save the returned id or task_id and poll every 5-15 seconds. Download only after status is completed or succeeded; failed means generation failed. A submission timeout may still have created a paid task: check task and usage records before resubmitting.'
        )}
      </p>
    </div>
  )
}

function Wan3VideoDocContent(props: {
  document: ApiDocDefinition
  baseUrl: string
  compact: boolean
}) {
  const { t } = useTranslation()
  const model = props.document.models[0]
  const samples = [
    {
      title: t('Text to video'),
      payload: {
        model,
        prompt:
          'A ginger cat sits by a window in warm afternoon light as the camera slowly moves closer.',
        duration: 2,
        metadata: {
          input: {
            prompt:
              'A ginger cat sits by a window in warm afternoon light as the camera slowly moves closer.',
          },
          parameters: {
            resolution: '480P',
            ratio: '16:9',
            duration: 2,
            audio: true,
            prompt_extend: true,
            watermark: false,
          },
        },
      },
    },
    {
      title: t('Mixed media references'),
      payload: {
        model,
        prompt:
          'Use the character from the image, the motion from the video, and the mood of the audio to create a coherent scene.',
        duration: 5,
        metadata: {
          input: {
            prompt:
              'Use the character from the image, the motion from the video, and the mood of the audio to create a coherent scene.',
            media: [
              {
                type: 'reference_image',
                url: 'https://media.example.com/reference/person.jpg',
              },
              {
                type: 'reference_video',
                url: 'https://media.example.com/reference/movement.mp4',
              },
              {
                type: 'reference_audio',
                url: 'https://media.example.com/reference/mood.mp3',
              },
            ],
          },
          parameters: {
            resolution: '720P',
            ratio: '16:9',
            duration: 5,
            audio: true,
            prompt_extend: true,
            watermark: false,
          },
        },
      },
    },
  ]
  const processingExample = JSON.stringify(
    {
      id: 'task_xxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxx',
      task_id: 'task_xxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxx',
      object: 'video',
      model,
      status: 'in_progress',
      progress: 45,
      created_at: 1789033559,
    },
    null,
    2
  )
  const completedExample = JSON.stringify(
    {
      id: 'task_xxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxx',
      object: 'video',
      model,
      status: 'completed',
      progress: 100,
      created_at: 1789033559,
      completed_at: 1789034154,
      metadata: {
        url: 'https://storage.example.com/file/generated-video.mp4',
      },
    },
    null,
    2
  )
  const failedExample = JSON.stringify(
    {
      id: 'task_xxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxx',
      object: 'video',
      model,
      status: 'failed',
      progress: 0,
      error: {
        code: 'task_failed',
        message: 'Task failed',
      },
    },
    null,
    2
  )

  return (
    <div className='flex flex-col gap-8'>
      <Alert>
        <Clock3 />
        <AlertTitle>{t('Wan3.0 video API integration guide')}</AlertTitle>
        <AlertDescription>
          {t(
            'Wan3.0 uses an asynchronous POST /v1/videos workflow. Poll the returned id every 5-10 seconds, read the completed video URL from the response, and save it promptly because the result is retained for about one hour.'
          )}
        </AlertDescription>
      </Alert>
      <section className='flex flex-col gap-4'>
        <SectionHeading icon={Video}>{t('Submit task')}</SectionHeading>
        {(props.compact ? samples.slice(0, 1) : samples).map((sample) => (
          <CodeSample
            key={sample.title}
            title={sample.title}
            code={curlWan3Submit(props.baseUrl, sample.payload)}
          />
        ))}
      </section>
      <section className='flex flex-col gap-4'>
        <SectionHeading>{t('Request parameters')}</SectionHeading>
        <ParameterTable rows={wan3VideoParameters()} />
      </section>
      <Alert>
        <BookOpenText />
        <AlertTitle>{t('Wan3.0 reference media')}</AlertTitle>
        <AlertDescription>
          {t(
            'Upload reference files to your own storage and send public HTTP(S) direct links in metadata.input.media. The gateway reads reference videos to measure billable duration before forwarding the URLs. Each request accepts up to 10 images, 5 videos, and 5 audio files; Base64 data URLs, file IDs, login pages, and links requiring custom authorization are not supported.'
          )}
        </AlertDescription>
      </Alert>
      <Alert>
        <AlertTriangle />
        <AlertTitle>{t('Wan3.0 billing and retries')}</AlertTitle>
        <AlertDescription>
          {t(
            'Billing uses the generation seconds plus actual reference video seconds: ¥0.37/s at 480P, ¥0.43/s at 720P, or ¥0.48/s at 1080P, multiplied by the applicable group factor. Reference images and audio are free; reference videos are billed by their actual total seconds at the selected resolution price. A POST timeout may still have created and billed a task; check task and usage records before submitting again.'
          )}
        </AlertDescription>
      </Alert>
      {!props.compact ? (
        <div className='flex flex-col gap-6'>
          <SectionHeading icon={Clock3}>{t('Query task')}</SectionHeading>
          <CodeSample
            title={t('Poll until final')}
            code={curlPoll(props.baseUrl)}
          />
          <section className='flex flex-col gap-3'>
            <h3 className='text-sm font-semibold'>{t('Response examples')}</h3>
            <Tabs defaultValue='processing' className='gap-3'>
              <TabsList>
                <TabsTrigger value='processing'>{t('Processing')}</TabsTrigger>
                <TabsTrigger value='completed'>{t('Completed')}</TabsTrigger>
                <TabsTrigger value='failed'>{t('Failed')}</TabsTrigger>
              </TabsList>
              <TabsContent value='processing'>
                <CodeBlock code={processingExample} language='json'>
                  <CodeBlockCopyButton />
                </CodeBlock>
              </TabsContent>
              <TabsContent value='completed'>
                <CodeBlock code={completedExample} language='json'>
                  <CodeBlockCopyButton />
                </CodeBlock>
              </TabsContent>
              <TabsContent value='failed'>
                <CodeBlock code={failedExample} language='json'>
                  <CodeBlockCopyButton />
                </CodeBlock>
              </TabsContent>
            </Tabs>
          </section>
          <StaticDataTable
            data={[
              {
                status: 'queued',
                meaning: t('Queued task: continue polling.'),
              },
              {
                status: 'in_progress',
                meaning: t('Generation in progress: continue polling.'),
              },
              {
                status: 'completed',
                meaning: t(
                  'Read metadata.url and save the video to permanent storage immediately.'
                ),
              },
              {
                status: 'failed',
                meaning: t('Read error.message and treat the task as failed.'),
              },
              {
                status: 'unknown',
                meaning: t(
                  'Keep the task id and poll again later; do not immediately create another task.'
                ),
              },
            ]}
            getRowKey={(row) => row.status}
            columns={[
              {
                id: 'status',
                header: t('Status'),
                cell: (row) => <code className='text-xs'>{row.status}</code>,
              },
              {
                id: 'meaning',
                header: t('Handling'),
                cell: (row) => row.meaning,
              },
            ]}
          />
          <CodeSample
            title={t('Download through the gateway')}
            code={[
              `curl -L ${props.baseUrl}/v1/videos/task_xxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxx/content \\`,
              '  -H "Authorization: Bearer $NEW_API_KEY" \\',
              '  -o wan-result.mp4',
            ].join('\n')}
          />
          <Alert>
            <AlertTriangle />
            <AlertTitle>
              {t('Save completed Wan3.0 videos promptly')}
            </AlertTitle>
            <AlertDescription>
              {t(
                'The result in metadata.url is temporary and is retained for about one hour. Download it directly without attaching your API Key, or use the authenticated /content endpoint. API-created tasks are not subject to the workbench three-download counter, but their result URL can still expire.'
              )}
            </AlertDescription>
          </Alert>
        </div>
      ) : null}
    </div>
  )
}

function megabyVideoParameters(): ParameterRow[] {
  return [
    {
      name: 'model',
      type: 'string',
      required: true,
      descriptionKey:
        'Choose one model ID from the SD model series table below. The request endpoint and field names are the same for every listed model.',
    },
    {
      name: 'prompt',
      type: 'string',
      required: true,
      descriptionKey: 'Required text prompt for the video.',
    },
    {
      name: 'duration | seconds',
      type: 'integer',
      required: false,
      descriptionKey:
        'Video duration in seconds. SD 2.0 and SD Mini models support 4 to 15 and default to 5; seedance-2.5 supports 3 to 30. duration takes precedence over seconds.',
    },
    {
      name: 'ratio',
      type: 'string',
      required: false,
      descriptionKey:
        'Output aspect ratio. Use a ratio supported by the selected model.',
    },
    {
      name: 'resolution',
      type: 'string',
      required: false,
      descriptionKey:
        'Output resolution. seedance-2.5 supports 720p or 480p. Every SD model has a fixed resolution encoded in its model ID.',
    },
    {
      name: 'referenceImages',
      type: 'string[]',
      required: false,
      descriptionKey:
        'Public http or https reference image URLs. SD 2.0 and SD Mini models accept up to 9; seedance-2.5 accepts up to 30.',
    },
    {
      name: 'referenceVideos',
      type: 'string[]',
      required: false,
      descriptionKey:
        'Public http or https reference video URLs. SD 2.0 and SD Mini models accept up to 3 URLs; their combined duration must not exceed 15 seconds. seedance-2.5 does not accept this field.',
    },
    {
      name: 'referenceAudios',
      type: 'string[]',
      required: false,
      descriptionKey:
        'Public http or https reference audio URLs. SD 2.0 and SD Mini models accept up to 3; seedance-2.5 accepts up to 10. Total duration must not exceed 15 seconds; MP3 or WAV is recommended.',
    },
  ]
}

function geminiImagePreviewParameters(): ParameterRow[] {
  return [
    {
      name: 'contents',
      type: 'object[]',
      required: true,
      descriptionKey:
        'Gemini contents. Each part may contain text, inlineData, or fileData. Use inlineData or fileData to provide reference images for editing. Generated images may be returned as Base64 inlineData or hosted fileData.fileUri URLs.',
    },
    {
      name: 'systemInstruction',
      type: 'object',
      required: false,
      descriptionKey:
        'Optional system instruction in the form { parts: [{ text }] }.',
    },
    {
      name: 'generationConfig.temperature',
      type: 'number',
      required: false,
      descriptionKey: 'Optional sampling temperature.',
    },
    {
      name: 'generationConfig.maxOutputTokens',
      type: 'integer',
      required: false,
      descriptionKey: 'Optional maximum output token count.',
    },
    {
      name: 'generationConfig.responseModalities',
      type: 'string[]',
      required: false,
      descriptionKey: 'Set to ["TEXT", "IMAGE"] for image output.',
    },
    {
      name: 'generationConfig.imageConfig.aspectRatio',
      type: 'string',
      required: false,
      descriptionKey:
        'Image aspect ratio, for example 1:1, 9:16, 16:9, 3:4, 4:3, 3:2, 2:3, 5:4, 4:5, or 21:9.',
    },
    {
      name: 'generationConfig.imageConfig.imageSize',
      type: 'string',
      required: false,
      descriptionKey:
        'Choose 1K, 2K, or 4K. GoogleFlow-ultra maps this value to its corresponding image model.',
    },
  ]
}

function gptImage2Parameters(modelName = 'gpt-image-2.5'): ParameterRow[] {
  const supportsAllResolutions =
    modelName === 'gpt-image-2.5-flare' ||
    modelName === 'gpt-image-2.5-sunburst'
  return [
    {
      name: 'model',
      type: 'string',
      required: true,
      descriptionKey: 'Use the model ID shown above.',
    },
    {
      name: 'prompt',
      type: 'string',
      required: true,
      descriptionKey: 'A text description of the requested image.',
    },
    {
      name: 'n',
      type: 'integer',
      required: false,
      descriptionKey:
        'Number of images to generate, from 1 to 10. Defaults to 1.',
    },
    {
      name: 'size',
      type: 'string',
      required: false,
      descriptionKey:
        'Pixel dimensions such as 1024x1024. When provided, this field takes precedence.',
    },
    {
      name: 'quality',
      type: supportsAllResolutions
        ? 'auto | low | medium | high | xhigh | max'
        : 'standard | hd',
      required: false,
      descriptionKey: supportsAllResolutions
        ? 'Thinking intensity for Flare and Sunburst. Choose auto, low, medium, high, xhigh, or max.'
        : 'Image quality. For 1K, only standard or hd is accepted; omitted quality defaults to standard, and any value other than hd is normalized to standard.',
    },
    {
      name: 'size_tier | resolution',
      type: supportsAllResolutions ? '1k | 2k | 4k' : '1k',
      required: false,
      descriptionKey: supportsAllResolutions
        ? 'Output resolution tier. Flare and Sunburst support 1k, 2k, and 4k when routed through the gptImage 官Key group.'
        : 'Output resolution tier. For the 1K channel, only 1k is accepted; 2k and 4k are rejected unless the request is routed to a high-resolution channel group.',
    },
    {
      name: 'aspect_ratio | ratio',
      type: 'string',
      required: false,
      descriptionKey:
        '1:1, 16:9, 9:16, 4:3, 3:4, 3:2, or 2:3. ratio is a compatible alias.',
    },
    {
      name: 'output_format',
      type: 'string',
      required: false,
      descriptionKey: 'jpeg, png, or webp. Defaults to jpeg.',
    },
    {
      name: 'background',
      type: 'string',
      required: false,
      descriptionKey:
        'Use transparent for a transparent background; PNG is used automatically.',
    },
    {
      name: 'referenceImages',
      type: 'string[]',
      required: false,
      descriptionKey:
        'Reference image URLs or data URIs. Provide at most 10 images.',
    },
    {
      name: 'response_format',
      type: 'string',
      required: false,
      descriptionKey: 'url or b64_json. Defaults to url.',
    },
  ]
}

function gptImage2EditParameters(modelName = 'gpt-image-2.5'): ParameterRow[] {
  return [
    ...gptImage2Parameters(modelName).filter(
      (parameter) => parameter.name !== 'referenceImages'
    ),
    {
      name: 'image | images',
      type: 'file | file[]',
      required: true,
      descriptionKey:
        'For multipart/form-data edits, upload one or more reference images. Provide at most 10 files, each no larger than 20 MB.',
    },
    {
      name: 'image_url',
      type: 'string | string[]',
      required: true,
      descriptionKey:
        'For JSON edits, provide one or more reference image URLs or data URIs. Provide at most 10 images.',
    },
    {
      name: 'mask',
      type: 'file | string',
      required: false,
      descriptionKey:
        'Optional mask image. Use a multipart file or a data URI in a JSON request.',
    },
  ]
}

function gptImage2HDParameters(): ParameterRow[] {
  return [
    {
      name: 'model',
      type: 'string',
      required: true,
      descriptionKey:
        'The gpt-image-2 model ID. Resolution support depends on the channel group.',
    },
    {
      name: 'prompt',
      type: 'string',
      required: true,
      descriptionKey: 'A text description of the requested image.',
    },
    {
      name: 'n',
      type: 'integer',
      required: false,
      descriptionKey:
        'Number of images to generate, from 1 to 10. Defaults to 1.',
    },
    {
      name: 'size',
      type: 'string',
      required: false,
      descriptionKey:
        'Output dimensions. 1K covers dimensions up to 1024, 2K up to 2048, and 4K up to 4096; examples include 1024x1024, 2048x2048, 3840x2160, and 2160x3840.',
    },
    {
      name: 'size_tier | resolution',
      type: '1k | 2k | 4k',
      required: false,
      descriptionKey:
        'Compatibility aliases for the output tier when size is omitted. When sent with size, the values must describe the same tier.',
    },
    {
      name: 'quality | background | output_format',
      type: 'string',
      required: false,
      descriptionKey:
        'OpenAI image parameters passed through to the upstream model when supported.',
    },
    {
      name: 'response_format',
      type: 'url | b64_json',
      required: true,
      descriptionKey:
        'Use url for a 24-hour temporary object-storage URL, especially for 2K or 4K images. Use b64_json only when the caller must receive image bytes directly.',
    },
    {
      name: 'stream',
      type: 'boolean',
      required: false,
      descriptionKey:
        'Do not set stream to true when a stable URL response is required. Streaming image events may contain base64 or data URLs.',
    },
  ]
}

function gptImage2HDEditParameters(): ParameterRow[] {
  return [
    ...gptImage2HDParameters().filter(
      (parameter) => parameter.name !== 'prompt'
    ),
    {
      name: 'prompt',
      type: 'string',
      required: true,
      descriptionKey: 'Editing instruction for the input image.',
    },
    {
      name: 'images[].image_url',
      type: 'string',
      required: false,
      descriptionKey:
        'For JSON edits, provide one or more source image URLs. The upstream service validates, downloads, and relays them before processing.',
    },
    {
      name: 'image | images',
      type: 'file | file[]',
      required: false,
      descriptionKey:
        'For multipart edits, upload one or more source image files.',
    },
    {
      name: 'mask.image_url | mask',
      type: 'string | file',
      required: false,
      descriptionKey:
        'Optional mask URL for JSON requests or mask file for multipart requests.',
    },
  ]
}

function responsesParameters(): ParameterRow[] {
  return [
    {
      name: 'model',
      type: 'string',
      required: true,
      descriptionKey: 'Use one of the listed GPT model IDs.',
    },
    {
      name: 'input',
      type: 'string | array',
      required: true,
      descriptionKey:
        'OpenAI Responses input. Provide a string or an array of input items.',
    },
    {
      name: 'max_output_tokens',
      type: 'integer',
      required: false,
      descriptionKey:
        'Caps the number of output tokens. Use this instead of max_completion_tokens or max_tokens.',
    },
    {
      name: 'stream',
      type: 'boolean',
      required: false,
      descriptionKey:
        'When true, returns OpenAI-compatible Server-Sent Events.',
    },
  ]
}

function claudeMessagesParameters(): ParameterRow[] {
  return [
    {
      name: 'model',
      type: 'string',
      required: true,
      descriptionKey: 'Use one of the listed Claude model IDs.',
    },
    {
      name: 'max_tokens',
      type: 'integer',
      required: true,
      descriptionKey: 'Maximum number of output tokens to generate.',
    },
    {
      name: 'messages',
      type: 'array',
      required: true,
      descriptionKey:
        'Anthropic Messages content. Each item includes a user or assistant role and content.',
    },
    {
      name: 'system',
      type: 'string or array',
      required: false,
      descriptionKey:
        'Optional system instruction. Do not add a system message to messages.',
    },
    {
      name: 'stream',
      type: 'boolean',
      required: false,
      descriptionKey:
        'When true, returns Anthropic-compatible Server-Sent Events.',
    },
  ]
}

function ParameterTable(props: { rows: ParameterRow[] }) {
  const { t } = useTranslation()
  return (
    <StaticDataTable
      data={props.rows}
      getRowKey={(row) => row.name}
      columns={[
        {
          id: 'name',
          header: t('Field'),
          cell: (row) => <code className='font-mono text-xs'>{row.name}</code>,
        },
        { id: 'type', header: t('Type'), cell: (row) => row.type },
        {
          id: 'required',
          header: t('Required'),
          cell: (row) => (row.required ? t('Required') : t('Optional')),
        },
        {
          id: 'description',
          header: t('Description'),
          cell: (row) => t(row.descriptionKey),
        },
      ]}
    />
  )
}

function SectionHeading(props: {
  icon?: React.ComponentType<{ className?: string }>
  children: React.ReactNode
}) {
  const Icon = props.icon
  return (
    <h2 className='flex items-center gap-2 text-lg font-semibold'>
      {Icon ? <Icon className='text-muted-foreground size-4' /> : null}
      {props.children}
    </h2>
  )
}

function CodeSample(props: { title: string; code: string; language?: string }) {
  return (
    <section className='flex flex-col gap-3'>
      <h3 className='text-sm font-semibold'>{props.title}</h3>
      <CodeBlock code={props.code} language={props.language ?? CODE_LANGUAGE}>
        <CodeBlockCopyButton />
      </CodeBlock>
    </section>
  )
}

function SeedanceOfficialDocContent(props: {
  baseUrl: string
  modelName: string
}) {
  const { t } = useTranslation()
  const payload = {
    model: props.modelName,
    content: [{ type: 'text', text: '电影质感的产品广告，镜头平稳推进' }],
    duration: 4,
    resolution: '720p',
    ratio: '16:9',
  }
  const endpoint = `${props.baseUrl}/api/v3/contents/generations/tasks`
  const referenceContent = [
    { type: 'text', text: '结合参考素材生成视频' },
    {
      type: 'image_url',
      image_url: { url: 'asset://image-id' },
      role: 'reference_image',
    },
    {
      type: 'video_url',
      video_url: { url: 'https://example.com/reference.mp4' },
      role: 'reference_video',
    },
    {
      type: 'audio_url',
      audio_url: { url: 'https://example.com/reference.mp3' },
      role: 'reference_audio',
    },
  ]

  return (
    <div className='flex flex-col gap-6'>
      <p className='text-muted-foreground text-sm'>
        {t(
          'Public models: seedance2.0, seedance2.0fast, seedance2.0mini, and seedance2.5. They use the default group and the same content-generation task protocol; only the model name changes.'
        )}
      </p>
      <p className='text-muted-foreground text-sm'>
        <code>
          seedance2.0 → doubao-seedance-2-0-260128; seedance2.0fast →
          doubao-seedance-2-0-fast-260128
        </code>
      </p>
      <p className='text-muted-foreground text-sm'>
        {t(
          'Seedance 2.0: 480p / 720p / 1080p / 4k, 4-15 seconds; Fast: 480p / 720p, 4-15 seconds; Mini: 480p / 720p / 1080p, 4-15 seconds; 2.5: 480p / 720p / 1080p, 4-30 seconds. Resolution is required; duration defaults to 4 seconds and ratio to 16:9. Availability also depends on the upstream account.'
        )}
      </p>
      <p className='text-muted-foreground text-sm'>
        {t(
          '支持比例：16:9、9:16、1:1、21:9、4:3、3:4。content 至少包含一个非空 text；可选 seed、generate_audio、watermark、return_last_frame、priority、execution_expires_after、tools、safety_identifier，按上游协议传递。'
        )}
      </p>
      <CodeSample
        title={t('创建任务')}
        code={[
          `curl -X POST ${endpoint} \\`,
          '  -H "Authorization: Bearer $NEW_API_KEY" \\',
          '  -H "Content-Type: application/json" \\',
          `  -d '${JSON.stringify(payload, null, 2)}'`,
        ].join('\n')}
      />
      <CodeSample
        title={t('参考素材：替换请求中的 content')}
        code={JSON.stringify(referenceContent, null, 2)}
        language='json'
      />
      <p className='text-muted-foreground text-sm'>
        {t(
          '素材支持公网 URL 和 asset:// 引用。图片 role 为 reference_image、first_frame 或 last_frame；首尾帧各最多 1 张，使用首尾帧时图片总数最多 2 张，且不能同时传参考视频。素材 ID 必须属于实际调用的上游渠道。'
        )}
      </p>
      <CodeSample
        title={t('创建响应')}
        code='{"id":"task_public"}'
        language='json'
      />
      <CodeSample
        title={t('查询任务')}
        code={`curl ${endpoint}/task_public -H "Authorization: Bearer $NEW_API_KEY"`}
      />
      <CodeSample
        title={t('成功响应')}
        code={JSON.stringify(
          {
            id: 'task_public',
            model: props.modelName,
            status: 'succeeded',
            content: { video_url: 'https://example.com/result.mp4' },
            usage: { total_tokens: 93366 },
          },
          null,
          2
        )}
        language='json'
      />
      <p className='text-muted-foreground text-sm'>
        {t(
          '使用创建响应中的公开 id 每 5 秒查询：queued / processing 继续等待，succeeded 读取 content.video_url，failed 读取 error.message。仅成功且上游返回有效用量时，usage.total_tokens 才可作为最终用量。'
        )}
      </p>
      <p className='text-muted-foreground text-sm'>
        {t(
          'Seedance 2.0 costs CNY 30 per million tokens without reference video and CNY 18 with reference video; Fast costs CNY 24 without and CNY 14 with reference video. Input and output use the same rate at every supported resolution. The applicable account/group factor still applies.'
        )}
      </p>
      <p className='text-muted-foreground text-sm'>
        {t(
          '按 Token 计费配置时，任务先预扣，成功后按实际 Token 结算，失败按平台规则退款。官方参考价（元/百万 Tokens）：Mini 无视频 23、含视频 14；2.5 在 480p/720p 无视频 70、含视频 42，1080p 无视频 77、含视频 46。实际价格以后台配置为准。'
        )}
      </p>
      <p className='text-muted-foreground text-sm'>
        {t(
          'Canvas 视频生成节点中选择 Seedance 2.0 Mini 或 Seedance 2.5，连接文本和参考素材，设置时长、分辨率与比例后生成。Canvas 使用 /v1/videos 与 metadata.content 转发同一协议，查询 metadata.url 并复用视频下载和素材保存流程；需要当前令牌分组有对应模型权限。'
        )}
      </p>
    </div>
  )
}

function ReviewAlert(props: { document: ApiDocDefinition }) {
  const { t } = useTranslation()
  if (!props.document.id.startsWith('veo-')) return null

  return (
    <Alert>
      <AlertTriangle />
      <AlertTitle>{t('Content review guidance')}</AlertTitle>
      <AlertDescription>
        {t(
          'A content-review rejection is resolved by changing the prompt or reference assets, not by retrying the same request. Review faces, minors, protected characters or logos, sexual or violent content, medical claims, and political or religious material before submitting.'
        )}
      </AlertDescription>
    </Alert>
  )
}

function TaskStatusReference(props: {
  baseUrl: string
  model: string
  duration: number
  image?: boolean
}) {
  const { t } = useTranslation()
  const responseField = props.image ? 'image_url' : 'video_url'
  const processingStatus = props.image ? 'processing' : 'in_progress'
  return (
    <div className='flex flex-col gap-6'>
      <SectionHeading icon={Clock3}>{t('Query task')}</SectionHeading>
      <CodeSample
        title={t('Poll until final')}
        code={curlPoll(props.baseUrl)}
      />
      <section className='flex flex-col gap-3'>
        <h3 className='text-sm font-semibold'>{t('Response examples')}</h3>
        <Tabs defaultValue='processing' className='gap-3'>
          <TabsList>
            <TabsTrigger value='processing'>{t('Processing')}</TabsTrigger>
            <TabsTrigger value='completed'>{t('Completed')}</TabsTrigger>
            <TabsTrigger value='failed'>{t('Failed')}</TabsTrigger>
          </TabsList>
          <TabsContent value='processing'>
            <CodeBlock
              code={processingResponse(
                props.model,
                props.duration,
                props.image ?? false
              )}
              language='json'
            >
              <CodeBlockCopyButton />
            </CodeBlock>
          </TabsContent>
          <TabsContent value='completed'>
            <CodeBlock
              code={completedResponse(
                props.model,
                props.duration,
                responseField
              )}
              language='json'
            >
              <CodeBlockCopyButton />
            </CodeBlock>
          </TabsContent>
          <TabsContent value='failed'>
            <CodeBlock code={failedResponse(props.model)} language='json'>
              <CodeBlockCopyButton />
            </CodeBlock>
          </TabsContent>
        </Tabs>
      </section>
      <StaticDataTable
        data={[
          { status: 'queued', meaning: t('Queued task: continue polling.') },
          {
            status: processingStatus,
            meaning: t('Generation in progress: continue polling.'),
          },
          {
            status: 'completed',
            meaning: t(
              'Read video_url, image_url, url, or metadata.result_urls[0].'
            ),
          },
          {
            status: 'failed',
            meaning: t('Read error.message and treat the task as failed.'),
          },
        ]}
        getRowKey={(row) => row.status}
        columns={[
          {
            id: 'status',
            header: t('Status'),
            cell: (row) => <code>{row.status}</code>,
          },
          { id: 'meaning', header: t('Handling'), cell: (row) => row.meaning },
        ]}
      />
    </div>
  )
}

function VideoDocContent(props: {
  document: ApiDocDefinition
  baseUrl: string
  compact: boolean
}) {
  const { t } = useTranslation()
  const duration = props.document.duration ?? 10
  const resolution = '4k'
  const model = props.document.models[0]
  const samples: { title: string; payload: Record<string, unknown> }[] = []
  const isVeo31 = props.document.id.startsWith('veo-3-1')
  const isVideoEdit = props.document.id.startsWith('veo-omni-flash-video-edit')

  if (isVeo31) {
    samples.push(
      {
        title: t('Submit task'),
        payload: {
          model,
          prompt:
            'a cinematic product reveal, smooth camera movement, natural lighting',
          duration,
          resolution,
          aspect_ratio: '16:9',
        },
      },
      {
        title: t('Image-to-video'),
        payload: {
          model,
          prompt: 'the subject turns toward the camera, cinematic lighting',
          duration,
          resolution,
          aspect_ratio: '9:16',
          images: ['https://your-cdn.com/first-frame.jpg'],
        },
      },
      {
        title: t('First and last frame'),
        payload: {
          model,
          prompt:
            'animate from the first frame to the last frame with smooth natural motion',
          duration,
          resolution,
          aspect_ratio: '16:9',
          images: [
            'https://your-cdn.com/first-frame.jpg',
            'https://your-cdn.com/last-frame.jpg',
          ],
        },
      },
      {
        title: t('Multiple references'),
        payload: {
          model,
          prompt: 'combine these references into one polished brand video',
          duration,
          resolution,
          aspect_ratio: '16:9',
          Ingredients_images: [
            'https://your-cdn.com/ref-1.jpg',
            'https://your-cdn.com/ref-2.jpg',
            'https://your-cdn.com/ref-3.jpg',
          ],
        },
      }
    )
  } else if (isVideoEdit) {
    samples.push({
      title: t('Submit task'),
      payload: {
        model,
        prompt: 'make the reference video brighter and more cinematic',
        duration,
        resolution,
        aspect_ratio: '16:9',
        video_url: 'https://your-cdn.com/reference-video.mp4',
        Ingredients_images: ['https://your-cdn.com/product.jpg'],
      },
    })
  } else {
    samples.push({
      title: t('Submit task'),
      payload: {
        model,
        prompt:
          'combine these references into a cinematic product launch video',
        duration,
        resolution,
        aspect_ratio: '16:9',
        Ingredients_images: [
          'https://your-cdn.com/ref-1.jpg',
          'https://your-cdn.com/ref-2.jpg',
        ],
      },
    })
  }

  const visibleSamples = props.compact ? samples.slice(0, 1) : samples

  return (
    <div className='flex flex-col gap-8'>
      <ReviewAlert document={props.document} />
      <Alert>
        <Clock3 />
        <AlertTitle>{t('Fixed duration')}</AlertTitle>
        <AlertDescription>
          固定时长为 {duration} 秒；分辨率通过 <code>resolution</code> 选择
          720p、1080p 或 4k。
        </AlertDescription>
      </Alert>
      <section className='flex flex-col gap-4'>
        <SectionHeading icon={Video}>{t('Submit task')}</SectionHeading>
        {visibleSamples.map((sample) => (
          <CodeSample
            key={sample.title}
            title={sample.title}
            code={curlSubmit(props.baseUrl, sample.payload)}
          />
        ))}
      </section>
      <section className='flex flex-col gap-4'>
        <SectionHeading>{t('Request parameters')}</SectionHeading>
        <ParameterTable rows={videoParameters(props.document)} />
      </section>
      {!props.compact ? (
        <TaskStatusReference
          baseUrl={props.baseUrl}
          model={model}
          duration={duration}
        />
      ) : null}
    </div>
  )
}

function Sd25VideoDocContent(props: {
  document: ApiDocDefinition
  baseUrl: string
  compact: boolean
}) {
  const { t } = useTranslation()
  const model = props.document.models[0]
  const samples = [
    {
      title: t('Text to video'),
      payload: {
        model,
        prompt:
          'A cinematic city night scene, a person walks along the street while the camera follows smoothly.',
        duration: 8,
        aspect_ratio: '16:9',
      },
    },
    {
      title: t('Multimodal references'),
      payload: {
        model,
        prompt:
          'Create a coherent product video using the character, visual style, motion, and voice references.',
        duration: 12,
        aspect_ratio: '16:9',
        extra_images: [
          'https://your-cdn.com/character.jpg',
          'https://your-cdn.com/style.jpg',
        ],
        extra_videos: ['https://your-cdn.com/motion-reference.mp4'],
        extra_audios: ['https://your-cdn.com/voice-reference.mp3'],
      },
    },
  ]
  const visibleSamples = props.compact ? samples.slice(0, 1) : samples

  return (
    <div className='flex flex-col gap-8'>
      <Alert>
        <BookOpenText />
        <AlertTitle>{t('SD 2.5 720p Standard request format')}</AlertTitle>
        <AlertDescription>
          {t(
            'Use POST /v1/videos to create an asynchronous task. All reference materials must be publicly accessible http or https URLs; binary file uploads and Base64 data are not supported.'
          )}
        </AlertDescription>
      </Alert>
      <section className='flex flex-col gap-4'>
        <SectionHeading icon={Video}>{t('Submit task')}</SectionHeading>
        {visibleSamples.map((sample) => (
          <CodeSample
            key={sample.title}
            title={sample.title}
            code={curlSubmit(props.baseUrl, sample.payload)}
          />
        ))}
      </section>
      <section className='flex flex-col gap-4'>
        <SectionHeading>{t('Request parameters')}</SectionHeading>
        <ParameterTable rows={sd25VideoParameters()} />
      </section>
      {!props.compact ? (
        <TaskStatusReference
          baseUrl={props.baseUrl}
          model={model}
          duration={8}
        />
      ) : null}
    </div>
  )
}

function MiniMaxH3VideoDocContent(props: {
  document: ApiDocDefinition
  baseUrl: string
  compact: boolean
}) {
  const { t } = useTranslation()
  const model = props.document.models[0]
  const samples = [
    {
      title: t('Text and reference media'),
      payload: {
        model,
        prompt: '参考图片中的主体，参考视频中的运动方式，声音参考音频素材。',
        duration: 4,
        resolution: '768P',
        aspect_ratio: '16:9',
        generation_mode: 'references',
        image_urls: ['https://media.example.com/character.png'],
        video_urls: ['https://media.example.com/motion.mp4'],
        audio_urls: ['https://media.example.com/voice.wav'],
        generate_audio: true,
      },
    },
    {
      title: t('MiniMax H3 first and last frames'),
      payload: {
        model,
        prompt: '从起始画面自然过渡到结束画面，动作流畅。',
        duration: 4,
        resolution: '768P',
        aspect_ratio: '16:9',
        generation_mode: 'frames',
        image_urls: [
          'https://media.example.com/first.png',
          'https://media.example.com/last.png',
        ],
        generate_audio: true,
      },
    },
    {
      title: t('MiniMax H3 advanced content'),
      payload: {
        model,
        duration: 4,
        resolution: '2K',
        ratio: 'adaptive',
        content: [
          { type: 'text', text: '主体参考图片，动作参考视频，声音参考音频。' },
          {
            type: 'image_url',
            image_url: { url: 'https://media.example.com/character.png' },
            role: 'reference_image',
          },
          {
            type: 'video_url',
            video_url: { url: 'https://media.example.com/motion.mp4' },
            role: 'reference_video',
          },
          {
            type: 'audio_url',
            audio_url: { url: 'https://media.example.com/voice.wav' },
            role: 'reference_audio',
          },
        ],
        generate_audio: true,
      },
    },
  ]
  const visibleSamples = props.compact ? samples.slice(0, 1) : samples

  return (
    <div className='flex flex-col gap-8'>
      <Alert>
        <BookOpenText />
        <AlertTitle>{t('MiniMax H3 API requirements')}</AlertTitle>
        <AlertDescription>
          {t(
            'Use POST /v1/videos with an API Key. MiniMax H3 accepts 4-15 second videos at 768P or 2K. It supports up to 9 reference images, 3 reference videos, and 3 reference audios; all reference media must be publicly accessible URLs.'
          )}
        </AlertDescription>
      </Alert>
      <section className='flex flex-col gap-4'>
        <SectionHeading icon={Video}>{t('Submit task')}</SectionHeading>
        {visibleSamples.map((sample) => (
          <CodeSample
            key={sample.title}
            title={sample.title}
            code={curlSubmit(props.baseUrl, sample.payload)}
          />
        ))}
      </section>
      <section className='flex flex-col gap-4'>
        <SectionHeading>{t('Request parameters')}</SectionHeading>
        <ParameterTable rows={miniMaxH3VideoParameters()} />
      </section>
      <Alert>
        <AlertTriangle />
        <AlertTitle>{t('MiniMax H3 material and pricing rules')}</AlertTitle>
        <AlertDescription>
          {t(
            'Images: the first 5 are free and images 6-9 cost ¥0.05 each. Reference videos: up to 3 files, each 2-15 seconds, with a combined maximum of 15 seconds; charge ¥0.09 per second at 768P or ¥0.15 per second at 2K. Reference audio: up to 3 files with a combined maximum of 15 seconds and no surcharge. Output video costs ¥0.23 per second at 768P or ¥0.30 per second at 2K.'
          )}
        </AlertDescription>
      </Alert>
      <Alert>
        <BookOpenText />
        <AlertTitle>{t('Idempotent task creation')}</AlertTitle>
        <AlertDescription>
          {t(
            'Send a globally unique Idempotency-Key header with each POST /v1/videos request. Reuse the same key after a timeout or uncertain 5xx response to retrieve the original task without creating another generation. Use a new key only when intentionally creating a new task.'
          )}
        </AlertDescription>
      </Alert>
      {!props.compact ? (
        <TaskStatusReference
          baseUrl={props.baseUrl}
          model={model}
          duration={4}
        />
      ) : null}
    </div>
  )
}

function MegabyVideoDocContent(props: {
  document: ApiDocDefinition
  baseUrl: string
  compact: boolean
}) {
  const { t } = useTranslation()
  const model = props.document.models[0]
  const modelRows = [
    ['sd-2.0-480p', '4-15', '480p fixed', '9 images, 3 videos, 3 audio'],
    ['sd-2.0-720p', '4-15', '720p fixed', '9 images, 3 videos, 3 audio'],
    ['sd-2.0-1080p', '4-15', '1080p fixed', '9 images, 3 videos, 3 audio'],
    ['sd-2.0-4k', '4-15', '4k fixed', '9 images, 3 videos, 3 audio'],
    ['sd-mini-480p', '4-15', '480p fixed', '9 images, 3 videos, 3 audio'],
    ['sd-mini-720p', '4-15', '720p fixed', '9 images, 3 videos, 3 audio'],
    ['seedance-2.5', '3-30', '480p, 720p', '30 images, no videos, 10 audio'],
  ]
  const sample = {
    title: t('Create video task'),
    payload: {
      model,
      prompt:
        'Create a coherent short product film using the supplied visual, motion, and music references.',
      duration: 8,
      ratio: '16:9',
      resolution: '720p',
      referenceImages: ['https://your-cdn.com/product.jpg'],
      referenceVideos: ['https://your-cdn.com/motion-reference.mp4'],
      referenceAudios: ['https://your-cdn.com/music-reference.mp3'],
    },
  }
  return (
    <div className='flex flex-col gap-8'>
      <Alert>
        <BookOpenText />
        <AlertTitle>{t('SD model series API integration guide')}</AlertTitle>
        <AlertDescription>
          {t(
            'Use https://huishitu.cn/v1/videos to create an asynchronous task and GET /v1/videos/{task_id} to poll it. Every model below uses this same request format; change only model and follow its capability limits. Query GET /v1/models before production submission because available models and abilities can change.'
          )}
        </AlertDescription>
      </Alert>
      <section className='flex flex-col gap-4'>
        <SectionHeading>{t('Supported models')}</SectionHeading>
        <StaticDataTable
          data={modelRows}
          getRowKey={(row) => row[0]}
          columns={[
            {
              id: 'model',
              header: t('Model'),
              cell: (row) => <code>{row[0]}</code>,
            },
            { id: 'duration', header: t('Duration'), cell: (row) => row[1] },
            {
              id: 'resolution',
              header: t('Resolution'),
              cell: (row) => row[2],
            },
            {
              id: 'references',
              header: t('Reference materials'),
              cell: (row) => row[3],
            },
          ]}
        />
      </section>
      <section className='flex flex-col gap-4'>
        <SectionHeading icon={Video}>{t('Submit task')}</SectionHeading>
        <CodeSample
          title={sample.title}
          code={curlSubmit(props.baseUrl, sample.payload)}
        />
      </section>
      <section className='flex flex-col gap-4'>
        <SectionHeading>{t('Request parameters')}</SectionHeading>
        <ParameterTable rows={megabyVideoParameters()} />
      </section>
      <Alert>
        <BookOpenText />
        <AlertTitle>{t('Idempotent task creation')}</AlertTitle>
        <AlertDescription>
          {t(
            'Send a globally unique Idempotency-Key header with each POST /v1/videos request. Reuse the same key after a timeout or uncertain 5xx response to retrieve the original task without creating another generation. Use a new key only when intentionally creating a new task.'
          )}
        </AlertDescription>
      </Alert>
      {!props.compact ? (
        <TaskStatusReference
          baseUrl={props.baseUrl}
          model={model}
          duration={8}
        />
      ) : null}
    </div>
  )
}

function GeminiImagePreviewDocContent(props: {
  baseUrl: string
  compact: boolean
  modelName: string
}) {
  const { t } = useTranslation()

  return (
    <div className='flex flex-col gap-7'>
      <Alert>
        <BookOpenText />
        <AlertTitle>{t('Gemini GenerateContent API')}</AlertTitle>
        <AlertDescription>
          {t(
            'This GoogleFlow-ultra image model returns results synchronously. Send the Gemini generateContent request body. Read generated images from inlineData.data when Base64 is returned, or from fileData.fileUri when a hosted image URL is returned.'
          )}
        </AlertDescription>
      </Alert>
      <section className='flex flex-col gap-4'>
        <SectionHeading>{t('Generate or edit image')}</SectionHeading>
        <CodeSample
          title={t('GenerateContent request')}
          code={curlGeminiImageGenerate(props.baseUrl, props.modelName)}
        />
      </section>
      <section className='flex flex-col gap-4'>
        <SectionHeading>{t('Request parameters')}</SectionHeading>
        <ParameterTable rows={geminiImagePreviewParameters()} />
      </section>
      {!props.compact ? (
        <section className='flex flex-col gap-4'>
          <SectionHeading>{t('Response examples')}</SectionHeading>
          <CodeSample
            title={t('Base64 response')}
            language='json'
            code={JSON.stringify(
              {
                candidates: [
                  {
                    content: {
                      role: 'model',
                      parts: [
                        {
                          inlineData: {
                            mimeType: 'image/png',
                            data: 'iVBORw0KGgoAAAANSUhEUgAA...',
                          },
                        },
                      ],
                    },
                    finishReason: 'STOP',
                    index: 0,
                  },
                ],
              },
              null,
              2
            )}
          />
          <CodeSample
            title={t('Hosted URL response')}
            language='json'
            code={JSON.stringify(
              {
                candidates: [
                  {
                    content: {
                      role: 'model',
                      parts: [
                        {
                          fileData: {
                            mimeType: 'image/png',
                            fileUri:
                              'https://cdn.example.com/generated/result.png',
                          },
                        },
                      ],
                    },
                    finishReason: 'STOP',
                    index: 0,
                  },
                ],
              },
              null,
              2
            )}
          />
        </section>
      ) : null}
    </div>
  )
}

function GptImage2DocContent(props: {
  baseUrl: string
  compact: boolean
  modelName?: string
}) {
  const { t } = useTranslation()
  const model = props.modelName || 'gpt-image-2'
  const supportsAllResolutions =
    model === 'gpt-image-2.5-flare' || model === 'gpt-image-2.5-sunburst'

  return (
    <div className='flex flex-col gap-7'>
      <Alert>
        <AlertTriangle />
        <AlertTitle>
          {t(supportsAllResolutions ? '1K, 2K, or 4K output' : '1K only')}
        </AlertTitle>
        <AlertDescription>
          {t(
            supportsAllResolutions
              ? 'Flare and Sunburst support 1K, 2K, and 4K output through the gptImage 官Key group. Quality accepts auto, low, medium, high, xhigh, or max.'
              : 'Only 1K output is available for this model. Set size_tier, quality, or resolution to 1k; do not send 2k or 4k.'
          )}
        </AlertDescription>
      </Alert>
      <Tabs defaultValue='generate' className='gap-6'>
        <TabsList>
          <TabsTrigger value='generate'>{t('Generate image')}</TabsTrigger>
          <TabsTrigger value='edit'>{t('Edit image')}</TabsTrigger>
        </TabsList>
        <TabsContent value='generate' className='flex flex-col gap-7'>
          <section className='flex flex-col gap-4'>
            <SectionHeading>{t('Generate image')}</SectionHeading>
            <CodeSample
              title={t('Minimal request')}
              code={curlImageSubmit(props.baseUrl, {
                model,
                prompt:
                  'product photography, white background, soft studio lighting',
                n: 1,
                size: supportsAllResolutions ? '2048x2048' : '1024x1024',
                size_tier: supportsAllResolutions ? '2k' : '1k',
                ...(supportsAllResolutions ? { quality: 'high' } : {}),
                response_format: 'url',
              })}
            />
          </section>
          <section className='flex flex-col gap-4'>
            <SectionHeading>{t('Generation parameters')}</SectionHeading>
            <ParameterTable rows={gptImage2Parameters(model)} />
          </section>
        </TabsContent>
        <TabsContent value='edit' className='flex flex-col gap-7'>
          <section className='flex flex-col gap-4'>
            <SectionHeading>{t('Edit image')}</SectionHeading>
            <CodeSample
              title={t('Multipart request')}
              code={curlImageEdit(props.baseUrl, model)}
            />
          </section>
          <section className='flex flex-col gap-4'>
            <SectionHeading>{t('Editing parameters')}</SectionHeading>
            <ParameterTable rows={gptImage2EditParameters(model)} />
          </section>
        </TabsContent>
      </Tabs>
      {!props.compact ? (
        <CodeSample
          title={t('Response example')}
          language='json'
          code={JSON.stringify(
            {
              created: 1710000000,
              data: [{ url: 'https://example.com/generated/image.png' }],
            },
            null,
            2
          )}
        />
      ) : null}
    </div>
  )
}

function GptImage2HDDocContent(props: { baseUrl: string; compact: boolean }) {
  const { t } = useTranslation()
  const model = 'gpt-image-2'

  return (
    <div className='flex flex-col gap-7'>
      <Alert>
        <AlertTriangle />
        <AlertTitle>{t('One request format for every resolution')}</AlertTitle>
        <AlertDescription>
          {t(
            'Use size or size_tier (1k, 2k, 4k) in the same request format. The gateway converts these fields, quality aliases, aspect ratio aliases, and JSON or multipart images to the selected upstream channel.'
          )}
        </AlertDescription>
      </Alert>
      <Tabs defaultValue='generate' className='gap-6'>
        <TabsList>
          <TabsTrigger value='generate'>{t('Generate image')}</TabsTrigger>
          <TabsTrigger value='edit-url'>{t('Edit with URL')}</TabsTrigger>
          <TabsTrigger value='edit-upload'>{t('Edit with upload')}</TabsTrigger>
        </TabsList>
        <TabsContent value='generate' className='flex flex-col gap-7'>
          <section className='flex flex-col gap-4'>
            <SectionHeading>{t('Generate image')}</SectionHeading>
            <CodeSample
              title={t('2K URL request')}
              code={curlImageSubmit(props.baseUrl, {
                model,
                prompt: 'A clean product photo of a futuristic API dashboard',
                size: '2048x2048',
                size_tier: '2k',
                aspect_ratio: '1:1',
                response_format: 'url',
              })}
            />
          </section>
          <section className='flex flex-col gap-4'>
            <SectionHeading>{t('Generation parameters')}</SectionHeading>
            <ParameterTable rows={gptImage2HDParameters()} />
          </section>
        </TabsContent>
        <TabsContent value='edit-url' className='flex flex-col gap-7'>
          <section className='flex flex-col gap-4'>
            <SectionHeading>{t('Edit with URL')}</SectionHeading>
            <CodeSample
              title={t('2K URL edit request')}
              code={curlImageEditURL(props.baseUrl, model)}
            />
          </section>
          <section className='flex flex-col gap-4'>
            <SectionHeading>{t('Editing parameters')}</SectionHeading>
            <ParameterTable rows={gptImage2HDEditParameters()} />
          </section>
        </TabsContent>
        <TabsContent value='edit-upload' className='flex flex-col gap-7'>
          <section className='flex flex-col gap-4'>
            <SectionHeading>{t('Edit with upload')}</SectionHeading>
            <CodeSample
              title={t('Multipart request')}
              code={curlImageEdit(props.baseUrl, model)}
            />
          </section>
        </TabsContent>
      </Tabs>
      {!props.compact ? (
        <CodeSample
          title={t('URL response example')}
          language='json'
          code={JSON.stringify(
            {
              created: 1780000000,
              data: [{ url: 'https://media.example.com/uploads/outputs/...' }],
            },
            null,
            2
          )}
        />
      ) : null}
    </div>
  )
}

function ResponsesDocContent(props: {
  document: ApiDocDefinition
  baseUrl: string
}) {
  const { t } = useTranslation()
  const model = props.document.models[0]

  return (
    <div className='flex flex-col gap-7'>
      <Alert>
        <MessageSquareText />
        <AlertTitle>{t('OpenAI Responses API')}</AlertTitle>
        <AlertDescription>
          {t(
            'All listed models use POST /v1/responses. Send your site API Key with Authorization: Bearer.'
          )}
        </AlertDescription>
      </Alert>
      <section className='flex flex-col gap-4'>
        <SectionHeading icon={MessageSquareText}>
          {t('Create response')}
        </SectionHeading>
        <CodeSample
          title={t('Minimal request')}
          code={curlResponse(props.baseUrl, model)}
        />
      </section>
      <section className='flex flex-col gap-4'>
        <SectionHeading>{t('Supported models')}</SectionHeading>
        <StaticDataTable
          data={props.document.models.map((name) => ({ name }))}
          getRowKey={(row) => row.name}
          columns={[
            {
              id: 'model',
              header: t('Model'),
              cell: (row) => (
                <code className='font-mono text-xs'>{row.name}</code>
              ),
            },
          ]}
        />
      </section>
      <section className='flex flex-col gap-4'>
        <SectionHeading>{t('Request parameters')}</SectionHeading>
        <ParameterTable rows={responsesParameters()} />
      </section>
      <CodeSample
        title={t('Response example')}
        language='json'
        code={JSON.stringify(
          {
            id: 'resp_xxxxxxxxxxxxxxxx',
            object: 'response',
            status: 'completed',
            model,
            output: [
              {
                type: 'message',
                role: 'assistant',
                content: [{ type: 'output_text', text: 'OK' }],
              },
            ],
          },
          null,
          2
        )}
      />
    </div>
  )
}

function ClaudeMessagesDocContent(props: {
  document: ApiDocDefinition
  baseUrl: string
}) {
  const { t } = useTranslation()
  const model = props.document.models[0]

  return (
    <div className='flex flex-col gap-7'>
      <Alert>
        <MessageSquareText />
        <AlertTitle>{t('Official Claude Messages')}</AlertTitle>
        <AlertDescription>
          {t(
            'All listed models use POST /v1/messages with x-api-key and anthropic-version: 2023-06-01.'
          )}
        </AlertDescription>
      </Alert>
      <section className='flex flex-col gap-4'>
        <SectionHeading icon={MessageSquareText}>
          {t('Create message')}
        </SectionHeading>
        <CodeSample
          title={t('Minimal request')}
          code={curlClaudeMessage(props.baseUrl, model)}
        />
      </section>
      <section className='flex flex-col gap-4'>
        <SectionHeading>{t('Supported models')}</SectionHeading>
        <StaticDataTable
          data={props.document.models.map((name) => ({ name }))}
          getRowKey={(row) => row.name}
          columns={[
            {
              id: 'model',
              header: t('Model'),
              cell: (row) => (
                <code className='font-mono text-xs'>{row.name}</code>
              ),
            },
          ]}
        />
      </section>
      <section className='flex flex-col gap-4'>
        <SectionHeading>{t('Request parameters')}</SectionHeading>
        <ParameterTable rows={claudeMessagesParameters()} />
      </section>
      <CodeSample
        title={t('Response example')}
        language='json'
        code={JSON.stringify(
          {
            id: 'msg_xxxxxxxxxxxxxxxx',
            type: 'message',
            role: 'assistant',
            model,
            content: [{ type: 'text', text: 'OK' }],
            stop_reason: 'end_turn',
          },
          null,
          2
        )}
      />
    </div>
  )
}

export function ApiModelReference(props: {
  modelName: string
  compact?: boolean
}) {
  const { t } = useTranslation()
  const baseUrl = useBaseUrl()
  const document = findApiDocument(props.modelName)
  const compact = props.compact ?? false

  if (!document) return null

  const documentTitle = document.title
  let apiType = t('Asynchronous API')
  if (document.isResponses) {
    apiType = t('OpenAI Responses API')
  } else if (document.isAnthropic) {
    apiType = t('Official Claude Messages')
  } else if (document.isImageGeneration) {
    apiType = t('OpenAI Images API')
  } else if (document.isGeminiImageGeneration) {
    apiType = t('Gemini GenerateContent API')
  }
  let documentContent: React.ReactNode
  if (document.isResponses) {
    documentContent = (
      <ResponsesDocContent document={document} baseUrl={baseUrl} />
    )
  } else if (document.isAnthropic) {
    documentContent = (
      <ClaudeMessagesDocContent document={document} baseUrl={baseUrl} />
    )
  } else if (document.isGeminiImageGeneration) {
    documentContent = (
      <GeminiImagePreviewDocContent
        baseUrl={baseUrl}
        compact={compact}
        modelName={props.modelName}
      />
    )
  } else if (document.isSd25Video) {
    documentContent = (
      <Sd25VideoDocContent
        document={document}
        baseUrl={baseUrl}
        compact={compact}
      />
    )
  } else if (document.id === 'kling-4.0') {
    documentContent = <KlingVideoDocContent baseUrl={baseUrl} />
  } else if (document.isWan3Video) {
    documentContent = (
      <Wan3VideoDocContent
        document={document}
        baseUrl='https://huishitu.cn'
        compact={compact}
      />
    )
  } else if (document.isMiniMaxH3Video) {
    documentContent = (
      <MiniMaxH3VideoDocContent
        document={document}
        baseUrl={baseUrl}
        compact={compact}
      />
    )
  } else if (document.id === 'seedance-official') {
    documentContent = (
      <SeedanceOfficialDocContent
        baseUrl={baseUrl}
        modelName={props.modelName}
      />
    )
  } else if (document.isMegabyVideo) {
    documentContent = (
      <MegabyVideoDocContent
        document={document}
        baseUrl={baseUrl}
        compact={compact}
      />
    )
  } else if (document.id === 'gpt-image-2') {
    documentContent = (
      <GptImage2HDDocContent baseUrl={baseUrl} compact={compact} />
    )
  } else if (document.isImageGeneration) {
    documentContent = (
      <GptImage2DocContent
        baseUrl={baseUrl}
        compact={compact}
        modelName={props.modelName}
      />
    )
  } else {
    documentContent = (
      <VideoDocContent
        document={document}
        baseUrl={baseUrl}
        compact={compact}
      />
    )
  }
  return (
    <article className='flex flex-col gap-8'>
      <header className='flex flex-col gap-3'>
        <div className='flex flex-wrap items-center gap-2'>
          <Badge
            variant={
              document.isResponses || document.isAnthropic
                ? 'outline'
                : 'secondary'
            }
          >
            {apiType}
          </Badge>
          <code className='text-muted-foreground text-xs'>
            {props.modelName}
          </code>
        </div>
        <div>
          <h1 className={cn('font-semibold', compact ? 'text-lg' : 'text-2xl')}>
            {documentTitle} {t('API integration guide')}
          </h1>
          <p className='text-muted-foreground mt-2 max-w-3xl text-sm leading-6'>
            {t(document.summaryKey)}
          </p>
        </div>
      </header>
      {!document.isResponses &&
      !document.isAnthropic &&
      !document.isImageGeneration ? (
        <Alert>
          <AlertTriangle />
          <AlertTitle>{t('Result URL retention')}</AlertTitle>
          <AlertDescription>
            {document.id === 'seedance-official'
              ? t('视频链接有效期由上游决定，请在生成成功后及时保存。')
              : t(
                  'Generated result URLs are retained for 24 hours. Save them before they expire.'
                )}
          </AlertDescription>
        </Alert>
      ) : null}
      {documentContent}
      {compact ? (
        <Link
          to='/docs'
          search={{ model: document.id }}
          className='text-primary text-sm font-medium hover:underline'
        >
          {t('Open full API documentation')}
        </Link>
      ) : null}
    </article>
  )
}

export function ApiDocsPage(props: { selectedDocument: ApiDocId }) {
  const { t } = useTranslation()
  const document =
    API_DOCUMENTS.find((item) => item.id === props.selectedDocument) ??
    API_DOCUMENTS[0]

  return (
    <PublicLayout>
      <div className='mx-auto grid max-w-7xl gap-8 lg:grid-cols-[15rem_minmax(0,1fr)]'>
        <aside className='lg:sticky lg:top-20 lg:self-start'>
          <div className='flex flex-col gap-3'>
            <div className='flex items-center gap-2 px-1'>
              <BookOpenText className='text-muted-foreground size-4' />
              <h1 className='text-sm font-semibold'>
                {t('API Documentation')}
              </h1>
            </div>
            <nav
              className='flex gap-2 overflow-x-auto overscroll-contain pb-1 lg:max-h-[calc(100svh-7rem)] lg:flex-col lg:overflow-x-hidden lg:overflow-y-auto lg:pr-1'
              aria-label={t('API Documentation')}
            >
              {API_DOCUMENTS.map((item) => (
                <Link
                  key={item.id}
                  to='/docs'
                  search={{ model: item.id }}
                  className={cn(
                    'border-border text-muted-foreground hover:bg-muted flex shrink-0 items-center rounded-md border px-3 py-2 text-sm transition-colors lg:w-full',
                    item.id === document.id &&
                      'bg-muted text-foreground border-foreground/20 font-medium'
                  )}
                >
                  {item.title}
                </Link>
              ))}
            </nav>
          </div>
        </aside>
        <main className='min-w-0'>
          <ApiModelReference modelName={document.models[0]} />
        </main>
      </div>
    </PublicLayout>
  )
}
